import { CademyPlan, CademyPlans } from '@cademy-package/educator-plans';

export type PricingFeature = {
    label: string;
    tooltip?: string;
};

export type PricingPlan = {
    title: string;
    pricing: CademyPlan['PRICING'];
    cta: string;
    ctaLink: string;
    features: Array<PricingFeature>;
};

type PricingPlans = Omit<Record<CademyPlan['NAME'], PricingPlan>, 'Enterprise' | 'Starter'>;

export const PLANS: PricingPlans = {
    Free: {
        title: 'Free',
        pricing: CademyPlans.free.PRICING,
        cta: 'Start for Free',
        ctaLink: '/create-educator',
        features: [
            {
                label: 'All Core Features, Plus:',
            },
            {
                label: '5% Platform Fee*',
                tooltip: `You have the option to either cover the Cademy platform fee yourself or pass it on
                to your customers at checkout. Payments are processed through Stripe, PayPal, or
                Klarna, each of which applies its own fees, typically ranging from 1.5% to 3.2%. The
                Cademy platform fee is charged on top of these processing fees.`,
            },
            {
                label: '1 Admin User',
            },
            {
                label: '5 Published Courses',
            },
            {
                label: '14 Days of Forum History',
                tooltip:
                    'You can create forums on your courses to boost engagement. The Free plan will show only posts created in the last 14 days in your forum.',
            },
            {
                label: '100 Emails / Day',
            },
            {
                label: '1,000 Emails / Month',
            },
            {
                label: '0.5 GB Max Upload Size',
            },
            {
                label: '2 GB Total Storage',
            },
        ],
    },
    Pro: {
        title: 'Pro',
        pricing: CademyPlans.pro.PRICING,
        cta: 'Start Free Trial',
        ctaLink: '/create-educator',
        features: [
            {
                label: 'Everything in Free, Plus:',
            },
            {
                label: '0% Platform Fee*',
                tooltip:
                    'To take card payments through Cademy, you will need to use Stripe, PayPal, or Klarna. Their transaction fees range from 1.5% to 3.2%.',
            },
            {
                label: '3 Admins & Facilitators',
            },

            {
                label: '20 Published Courses',
            },
            {
                label: '60 Days of Forum History',
                tooltip:
                    'You can create forums on your courses to boost engagement. The Pro plan will show only posts created in the last 60 days in your forum.',
            },
            {
                label: '1,000 Emails / Day',
            },
            {
                label: '10,000 Emails / Month',
            },
            {
                label: '2 GB Max Upload Size',
            },
            {
                label: '20 GB Total Storage',
            },
            {
                label: 'Private Courses',
            },
            {
                label: 'Course Imports',
            },
            {
                label: 'Hide Cademy Logos',
            },
            {
                label: 'Custom Brand Colors',
                tooltip: 'Match your brand colors',
            },
            {
                label: 'Invoices & Manual Payments',
                tooltip:
                    'Allow customers to pay by requesting an invoice on checkout or by manual payment such as bank transfer, cash, external funding, etc.',
            },
            {
                label: 'Attachments on Notes',
            },
            {
                label: 'Zapier Integration',
            },
        ],
    },
    Teams: {
        title: 'Teams',
        pricing: CademyPlans.teams.PRICING,
        cta: 'Start Free Trial',
        ctaLink: '/create-educator',
        features: [
            {
                label: 'Everything in Pro, Plus:',
            },
            {
                label: '0% Platform Fee*',
                tooltip:
                    'To take card payments through Cademy, you will need to use Stripe, PayPal, or Klarna. Their transaction fees range from 1.5% to 3.2%.',
            },
            {
                label: '20 Admins & Facilitators',
            },
            {
                label: '100 Published Courses',
            },
            {
                label: 'Unlimited Forum History',
                tooltip:
                    'You can create forums on your courses to boost engagement. The Teams plan will show all posts created in your forum.',
            },
            {
                label: '10,000 Emails / Day',
            },
            {
                label: '100,000 Emails / Month',
            },
            {
                label: '5 GB Max Upload Size',
            },
            {
                label: '200 GB Total Storage',
            },
            {
                label: 'Custom Email Domains',
                tooltip: 'Send emails using your domain',
            },
            {
                label: 'SCORM Courses',
                tooltip:
                    'Create engaging courses with SCORM-compatible course builders and import them directly into Cademy.',
            },
            {
                label: 'Xero Integration',
            },
            {
                label: 'Migration Assistance',
            },
            {
                label: 'Training & Onboarding',
            },
            {
                label: 'Custom Features',
            },
            {
                label: 'Premium Support',
            },
        ],
    },
};
